/**
 * CR-6348: Order Details section of the reworked SO Lines filter panel.
 * Text/search/dropdown/checkbox fields plus the re-introduced legacy status
 * filters (ODD Status, OTD Status to LSPD) and Critical-lines-only.
 *
 * CR-4716 role-gating is preserved: Buyer ID / OTIF Overridden / PPV stay
 * hidden from supplier users even though the AI-generated diagram drew them —
 * "existing permissions must remain unchanged".
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  POLinesFilters,
  STATUS_OPTIONS,
  ODD_STATUS_OPTIONS,
  OTD_LSPD_STATUS_OPTIONS,
} from '../../types';
import { FilterSection, FIELD_CLS, LABEL_CLS } from '../fieldStyles';

interface OrderDetailsSectionProps {
  filters: POLinesFilters;
  onFilterChange: (key: string, value: string) => void;
  isSupplierUser: boolean;
  // CR-6347: the customer view renders Part Number / Description in its own
  // Classification section, so it hides them here. Defaults preserve the
  // supplier view exactly.
  showPartAndDescription?: boolean;
  // CR-6347: extra order-detail fields injected by the caller (customer view
  // adds ERP Order Type + On Hold, which are feature-flag gated upstream).
  extraFields?: ReactNode;
  /** CR-6347: Optional icon for the section header */
  icon?: LucideIcon;
}

export function OrderDetailsSection({
  filters,
  onFilterChange,
  isSupplierUser,
  showPartAndDescription = true,
  extraFields,
  icon,
}: OrderDetailsSectionProps) {
  return (
    <FilterSection title="Order Details" icon={icon}>
      {/* PO Number */}
      <div>
        <label className={LABEL_CLS}>PO Number</label>
        <input
          type="text"
          placeholder="620596..."
          value={filters.poNumber}
          onChange={(e) => onFilterChange('poNumber', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Line Number */}
      <div>
        <label className={LABEL_CLS}>Line #</label>
        <input
          type="text"
          placeholder="1, 2, 3..."
          value={filters.lineNumber}
          onChange={(e) => onFilterChange('lineNumber', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Batch / Serial No */}
      <div>
        <label className={LABEL_CLS}>Batch/Serial No</label>
        <input
          type="text"
          placeholder="Batch or serial..."
          value={filters.batchSerialNo}
          onChange={(e) => onFilterChange('batchSerialNo', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Buyer ID — CR-4716: hidden from supplier users */}
      {!isSupplierUser && (
        <div>
          <label className={LABEL_CLS}>Buyer ID</label>
          <input
            type="text"
            placeholder="Buyer ID..."
            value={filters.buyerId}
            onChange={(e) => onFilterChange('buyerId', e.target.value)}
            className={FIELD_CLS}
          />
        </div>
      )}

      {/* Part Number + Description — hidden in the customer view, which groups
          them under its own Classification section (CR-6347). */}
      {showPartAndDescription && (
        <>
          <div>
            <label className={LABEL_CLS}>Part Number</label>
            <input
              type="text"
              placeholder="Part number..."
              value={filters.partNumber}
              onChange={(e) => onFilterChange('partNumber', e.target.value)}
              className={FIELD_CLS}
            />
          </div>

          <div>
            <label className={LABEL_CLS}>Description</label>
            <input
              type="text"
              placeholder="Description..."
              value={filters.description}
              onChange={(e) => onFilterChange('description', e.target.value)}
              className={FIELD_CLS}
            />
          </div>
        </>
      )}

      {/* Status */}
      <div>
        <label className={LABEL_CLS}>Status</label>
        <select
          value={filters.status}
          onChange={(e) => onFilterChange('status', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Caller-injected fields (CR-6347 customer view: ERP Order Type, On Hold) */}
      {extraFields}

      {/* ODD Status (CR-6348) */}
      <div>
        <label className={LABEL_CLS}>ODD Status</label>
        <select
          value={filters.oddStatus}
          onChange={(e) => onFilterChange('oddStatus', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          {ODD_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* OTD Status to LSPD (CR-6348) */}
      <div>
        <label className={LABEL_CLS}>OTD Status to LSPD</label>
        <select
          value={filters.otdStatus}
          onChange={(e) => onFilterChange('otdStatus', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          {OTD_LSPD_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Next Action */}
      <div>
        <label className={LABEL_CLS}>Next Action</label>
        <select
          value={filters.nextAction}
          onChange={(e) => onFilterChange('nextAction', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          <option value="customer">Waiting Customer</option>
          <option value="supplier">Waiting Supplier</option>
        </select>
      </div>

      {/* OTIF Overridden — CR-4716: hidden from supplier users */}
      {!isSupplierUser && (
        <div>
          <label className={LABEL_CLS}>OTIF Overridden</label>
          <select
            value={filters.otifOverridden}
            onChange={(e) => onFilterChange('otifOverridden', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">All</option>
            <option value="yes">Overridden</option>
            <option value="no">Not Overridden</option>
          </select>
        </div>
      )}

      {/* PPV — CR-4716: hidden from supplier users */}
      {!isSupplierUser && (
        <div>
          <label className={LABEL_CLS}>PPV</label>
          <select
            value={filters.ppv}
            onChange={(e) => onFilterChange('ppv', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">All</option>
            <option value="positive">Positive (Cost Overrun)</option>
            <option value="negative">Negative (Savings)</option>
            <option value="zero">Zero (No Variance)</option>
          </select>
        </div>
      )}

      {/* Critical lines only (CR-6348) */}
      <div className="flex items-center pt-7">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.criticalOnly === 'true'}
            onChange={(e) => onFilterChange('criticalOnly', e.target.checked ? 'true' : '')}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
          />
          <span className="text-sm font-medium">Critical lines only</span>
        </label>
      </div>
    </FilterSection>
  );
}
